import {getCookie} from '@github-ui/cookies'

import {
  HIDDEN_SECTIONS_COOKIE_NAME,
  SECTION_ORDER_COOKIE_NAME,
} from '@github-ui/shared-dashboard-surface/constants/cookies'

/**
 * Helpers for inbox section preferences and the first-visible-section
 * SSR-blocking optimization.
 *
 * The Pulls inbox route fetches each section as a separate query. To keep
 * first-paint fast, only the first section query is registered as Blocking so the
 * SSR loader awaits its data before flushing HTML. By default that's
 * `review-requested`, but users who reorder or hide sections via the
 * Customize sections dialog will see a different section first. The backend
 * reads these same preference cookies and serializes the normalized payload on
 * subsequent requests.
 */

const MAX_COOKIE_BYTES = 1024 // Matches limit used by backend
const encoder = new TextEncoder()

export const DEFAULT_FIRST_INBOX_SECTION = 'review-requested'

type PreferenceIdsInput = readonly string[] | string | undefined

export interface NormalizedInboxSectionPreferences {
  sectionOrder: string[]
  hiddenSections: string[]
  firstVisibleSectionId: string
}

function uniqueIds(ids: readonly string[]) {
  const seen = new Set<string>()
  const normalized: string[] = []

  for (const id of ids) {
    if (seen.has(id)) continue
    seen.add(id)
    normalized.push(id)
  }

  return normalized
}

function parseSectionIds(value: PreferenceIdsInput): string[] {
  if (!value) return []

  if (typeof value !== 'string') {
    return uniqueIds(value.map(id => id.trim()).filter(Boolean))
  }

  if (encoder.encode(value).byteLength > MAX_COOKIE_BYTES) return []

  return uniqueIds(
    value
      .split(',')
      .map(id => id.trim())
      .filter(Boolean),
  )
}

function filterAvailableIds(ids: readonly string[], availableSet: ReadonlySet<string>) {
  return ids.filter(id => availableSet.has(id))
}

function appendMissingIds(ids: readonly string[], defaults: readonly string[]) {
  const seen = new Set(ids)
  const normalized = [...ids]

  for (const id of defaults) {
    if (seen.has(id)) continue
    seen.add(id)
    normalized.push(id)
  }

  return normalized
}

export function normalizeInboxSectionPreferences({
  order,
  hidden,
  availableIds,
  defaultOrder = availableIds,
}: {
  order?: PreferenceIdsInput
  hidden?: PreferenceIdsInput
  availableIds: readonly string[]
  defaultOrder?: readonly string[]
}): NormalizedInboxSectionPreferences {
  const availableSet = new Set(availableIds)
  const defaultAvailableOrder = filterAvailableIds(defaultOrder, availableSet)
  const savedOrder = filterAvailableIds(parseSectionIds(order), availableSet)
  const sectionOrder =
    savedOrder.length > 0 ? appendMissingIds(savedOrder, defaultAvailableOrder) : defaultAvailableOrder

  const savedHidden = filterAvailableIds(parseSectionIds(hidden), availableSet)
  const savedHiddenSet = new Set(savedHidden)

  // If a flag flip would hide every available section, unhide just the top of
  // the order so the user keeps the rest of their hidden prefs instead of
  // having them silently reset.
  const firstOrderId = sectionOrder[0]
  const allHidden = firstOrderId !== undefined && sectionOrder.every(id => savedHiddenSet.has(id))
  const hiddenSet = allHidden ? new Set([...savedHiddenSet].filter(id => id !== firstOrderId)) : savedHiddenSet
  const hiddenSections = allHidden ? savedHidden.filter(id => id !== firstOrderId) : savedHidden
  const firstVisibleSectionId =
    sectionOrder.find(id => !hiddenSet.has(id)) ?? defaultAvailableOrder[0] ?? DEFAULT_FIRST_INBOX_SECTION

  return {sectionOrder, hiddenSections, firstVisibleSectionId}
}

export function getSectionOrderFromCookie(): string[] {
  return parseSectionIds(getCookie(SECTION_ORDER_COOKIE_NAME)?.value)
}

export function getHiddenSectionsFromCookie(): string[] {
  return parseSectionIds(getCookie(HIDDEN_SECTIONS_COOKIE_NAME)?.value)
}

export function computeFirstVisibleInboxSection(order: string[], hidden: string[], availableIds?: string[]): string {
  const ids = availableIds ?? uniqueIds([...order, DEFAULT_FIRST_INBOX_SECTION])
  return normalizeInboxSectionPreferences({order, hidden, availableIds: ids}).firstVisibleSectionId
}

export function computeFirstVisibleInboxSectionFromCookies(availableIds?: string[]): string {
  const ids = availableIds ?? [DEFAULT_FIRST_INBOX_SECTION]
  return getInboxSectionPreferencesFromCookies(ids).firstVisibleSectionId
}

function getInboxSectionPreferencesFromCookies(availableIds: readonly string[]): NormalizedInboxSectionPreferences {
  return normalizeInboxSectionPreferences({
    order: getCookie(SECTION_ORDER_COOKIE_NAME)?.value,
    hidden: getCookie(HIDDEN_SECTIONS_COOKIE_NAME)?.value,
    availableIds,
  })
}

export function isBlockingInboxSection(sectionId: string, availableIds: () => string[]) {
  return () => computeFirstVisibleInboxSectionFromCookies(availableIds()) === sectionId
}

export function isDeferredInboxSection(sectionId: string, availableIds: () => string[]) {
  return () => {
    const preferences = getInboxSectionPreferencesFromCookies(availableIds())
    return (
      preferences.sectionOrder.includes(sectionId) &&
      preferences.firstVisibleSectionId !== sectionId &&
      !preferences.hiddenSections.includes(sectionId)
    )
  }
}
